import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { formatNumber, formatPercentage } from "../Charts/utils/formatNumber";
import {
  formatStockPrice,
  getDisplayCurrencySymbol,
} from "../../../utils/stockPriceFormatter";

export type FormatType = "percent" | "ratio" | "currency" | "price" | "number";

export const formatMetricValue = (
  value: number | null,
  format: FormatType,
  currency?: string,
): string => {
  if (value === null) return "—";
  switch (format) {
    case "percent": {
      const pct = value * 100;
      const sign = pct > 0 ? "+" : "";
      return `${sign}${formatPercentage(pct, 1)}%`;
    }
    case "ratio":
      return `${formatPercentage(value, 1)}x`;
    case "currency": {
      const symbol = getDisplayCurrencySymbol(currency);
      const abs = Math.abs(value);
      const formatted = formatNumber(abs);
      return value < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
    }
    case "price":
      return formatStockPrice({ price: value, currency });
    case "number":
      return value.toFixed(2);
    default:
      return "—";
  }
};

export interface MetricRowProps {
  label: string;
  value: number | null;
  format: FormatType;
  currency?: string;
  isWinner: boolean;
  winnerColor: string;
}

const MetricRow = ({
  label,
  value,
  format,
  currency,
  isWinner,
  winnerColor,
}: MetricRowProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Use theme surface color to match ViewTicker row styling
  const rowBg = isDark ? "#212121" : theme.palette.background.paper;
  const rowBorder = theme.palette.divider;
  const textPrimary = theme.palette.text.primary;

  const isPercent = format === "percent";
  const pctValue = isPercent && value !== null ? value * 100 : null;
  const isNegative = isPercent && pctValue !== null && pctValue < 0;

  const valueColor = isPercent && isNegative ? theme.palette.error.main : textPrimary;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: { sm: "space-between" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 0.25, sm: 0 },
        borderLeft: isWinner ? `3px solid ${winnerColor}` : "3px solid transparent",
        px: 2,
        py: 1.5,
        backgroundColor: rowBg,
        borderBottom: `1px solid ${rowBorder}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.6875rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: textPrimary,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.9375rem",
          fontWeight: 700,
          color: valueColor,
        }}
      >
        {formatMetricValue(value, format, currency)}
      </Typography>
    </Box>
  );
};

export default MetricRow;
